/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Form, Modal, Row, Select, Skeleton, Space, Typography, Tooltip } from 'antd';
import { ArrowRightOutlined, CheckCircleOutlined, CheckOutlined, CloseOutlined, InfoCircleOutlined, QuestionCircleOutlined, SyncOutlined } from '@ant-design/icons';
import BotAvatar from '../UI/BotAvatar';

// misc imports
import is from '../../utils/is';
import { useFetchBotsQuery } from '../../store/bots/botsApi.js';
import { useReplaceBotContentMutation } from '../../store/chapterSources/chapterSourcesApi';
import { useEffect } from 'react';

// Constant declarations
const { Option } = Select;

const REPLACEABLE_ITEMS_LABEL = ['Flows', 'Automated Answers', 'LiveChat System Messages', 'Knowledge Base Connections', 'Non-Constant Variables'];

const BotContentReplaceModal = ({
  targetBot,
  isModalOpen = false,
  closeModal = () => {}
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { orgId } = useParams();

  const sourceBotId = form.getFieldValue('sourceBotId');
  const targetBotId = form.getFieldValue('targetBotId');

  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId }, { skip: !orgId });
  const [replaceBotContent, { isLoading: isReplacingBotContent }] = useReplaceBotContentMutation();

  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false);
  const [showConflictMessage, setShowConflictMessage] = useState(false);

  const replaceContent = async () => {
    const targetBotId = form.getFieldValue('targetBotId');
    setIsUpdateSuccessful(false);
    const isUpdateSuccessful = await replaceBotContent({ orgId, tenantId: targetBotId, replacementTenantId: sourceBotId }).unwrap();
    if (isUpdateSuccessful) {
      setIsUpdateSuccessful(true);
    }
  };

  // Reset form when modal is closed or opened
  useEffect(() => {
    setIsUpdateSuccessful(false);
    setShowConflictMessage(false);
    return () => form.resetFields();
  }, [isModalOpen]);

  useEffect(() => {
    if(targetBot) {
      form.setFieldValue('targetBotId', targetBot.id);
    }
  }, [targetBot, isModalOpen]);

  const checkForLanguageConflicts = (botId) => {
    const selectedBot = bots.items?.find(bot => bot.id === botId);
    setShowConflictMessage(selectedBot?.settings?.languages?.primary !== targetBot.settings?.languages?.primary);
  };

  const renderProgressModalContents = () => {
    if (isReplacingBotContent) {
      return (
        <Row gutter={24} justify="center px-5 py-10">
          <Col xs={24} className="text-center">
            <Typography.Title level={5} className="mb-5">
              {t('Replacing Content')}
            </Typography.Title>

            <Typography.Paragraph className="mb-0">
              <SyncOutlined spin className="fs-xxl" />
            </Typography.Paragraph>

            <Typography.Title className="mb-5" level={5}>
              {t('Processing, please wait...')}
            </Typography.Title>

          </Col>
        </Row>
      );
    }

    if (isUpdateSuccessful) {
      return (
        <Row gutter={24} justify="center">
          <Col xs={24} className="p-7">
            <Typography.Paragraph className="mb-4 text-success text-center fs-xl fw-bold">
              <CheckCircleOutlined className="mr-3"/>
              {t('Succeed')}
            </Typography.Paragraph>
            <Row>
              <Col xs={24} className="mb-4 text-center">
                <Typography.Text className="fw-bold">
                  {t('Content has been replaced')}
                </Typography.Text>
              </Col>
              <Col span={24}>
                <Row className="mb-3">
                  <Col xs={12}>
                    <Typography.Text >
                      {<Trans t={t}><Typography.Text className="ml-8 fw-bold">from</Typography.Text></Trans>}
                    </Typography.Text>
                  </Col>
                  <Col xs={12}>
                    <Typography.Text >
                      {<Trans t={t}><Typography.Text className="ml-8 fw-bold">to</Typography.Text></Trans>}
                    </Typography.Text>
                  </Col>
                </Row>
                <Row className="bordered border-radius-base">
                  <Col xs={24} className="py-5">
                    <Row className="d-flex justify-content-center">
                      <Col xs={9} className="d-flex justify-content-center align-items-center">
                        <BotAvatar bot={(bots?.items || []).find(bot => bot.id === sourceBotId)} avatarSize={24} wrapBotName ellipsis={true} />
                      </Col>
                      <Col xs={2} className="d-flex align-items-center">
                        <ArrowRightOutlined className="fs-lg"/>
                      </Col>
                      <Col xs={9}>
                        <BotAvatar bot={(bots?.items || []).find(bot => bot.id === targetBotId)} avatarSize={24} wrapBotName className="ml-4" ellipsis={true} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      );
    }

    const renderReplacableItemsLabel = (label) => {
      return (
        <Col xs={24} className="mb-2">
          <Typography.Text>
            <CheckOutlined className="fs-sm mr-3" />
            {t(label)}
          </Typography.Text>
        </Col>
      );
    };
    // Else
    return (
      <Fragment>
        <Row className="bg-layout-gray border-radius-top-left-base border-radius-top-right-base p-4">
          <Col xs={20} className="d-flex align-items-center">
            <Typography.Title level={3} className="mb-0">
              {t('Replace Agent Content?', { context: 'AI agent' })}
            </Typography.Title>
          </Col>
          <Col xs={4} className="mt-2">
            <CloseOutlined className="float-right fs-xl cursor-pointer" onClick={() => {
              closeModal();
              setIsProgressModalOpen(false);
            }}
            />
          </Col>
        </Row>
        <Row gutter={24} justify="center" className="p-4 border-radius-bottom-left-base border-radius-bottom-right-base">
          <Col xs={24} className="d-flex flex-column align-items-center">

            <Typography.Paragraph className="text-warning mb-3 fs-lg">
              <Space>
                <InfoCircleOutlined />
                {t('Non Reversible Action!')}
              </Space>
            </Typography.Paragraph>

            <Typography.Paragraph className="mb-3 px-5 text-muted">
              {t('Replacing Agent content will overwrite all currently existing content of:', { context: 'AI agent' })}
            </Typography.Paragraph>
          </Col>

          <Col xs={24} className="px-8 d-flex flex-column justify-content-center">
            <Row>
              <Col xs={24} className="mb-5">
                <BotAvatar bot={(bots?.items || []).find(bot => bot.id === targetBotId)}/>
              </Col>
              <Col xs={24} className="pl-8">
                <Row>
                  {REPLACEABLE_ITEMS_LABEL.map((label) => {
                    return renderReplacableItemsLabel(label);
                  })}
                </Row>
              </Col>
            </Row>

          </Col>

          <Col xs={24} className="text-center mb-5 mt-3">
            <Typography.Text className="fw-700">
              {t('Are you sure?')}
            </Typography.Text>
          </Col>

          <Col xs={12} className="text-center">
            <Button
              size="large"
              block
              onClick={() => {
                setIsProgressModalOpen(false);
              }}
            >
              {t('No')}
            </Button>
          </Col>

          <Col xs={12} className="text-center">
            <Button
              size="large"
              type="primary"
              block
              onClick={replaceContent}
            >
              {t('Replace')}
            </Button>
          </Col>
        </Row>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Modal
        title={
          <Typography.Text>
            <SyncOutlined rotate={120} className="mr-2"/>
            {t('Replace Agent Content', { context: 'AI agent' })}
          </Typography.Text>
        }
        open={isModalOpen && !isProgressModalOpen}
        onCancel={closeModal}
        footer={null}
        style={{ maxWidth: 600 }}
        width={'95%'}
        centered
        destroyOnClose
      >
        <Row gutter={24}>
          <Col xs={24}>
            <Form
              wrapperCol={{ span: 24 }}
              name="botContentReplaceForm"
              layout="vertical"
              onFinish={() => setIsProgressModalOpen(true)}
              initialValues={{
                sourceBotId: null
              }}
              form={form}
              requiredMark={false}
            >
              <Row gutter={16} className="mb-4">
                <Col xs={11}>
                  <Form.Item
                    name="sourceBotId"
                    label={
                      <Row>
                        <Col span={24}>
                          <Typography.Text >
                            {<Trans t={t}><Typography.Text className="text-secondary ml-3">From</Typography.Text></Trans>}
                          </Typography.Text>
                        </Col>
                      </Row>
                    }
                    rules={[{
                      required: true,
                      message: t('Please choose a source agent.', { context: 'AI agent' })
                    }]}
                    validateStatus={showConflictMessage ? 'warning' : undefined}
                    help={showConflictMessage
                      ? t('This agent has a different primary language than the current one, which may lead to inconsistencies or issues in certain parts of the content.', { context: 'AI agent' })
                      : undefined
                    }
                  >
                    <Select
                      placeholder={t('Choose an agent', { context: 'AI agent' })}
                      size="large"
                      className="fs-base mb-1"
                      loading={isFetchingBots}
                      showSearch
                      onChange={checkForLanguageConflicts}
                      filterOption={(input, option) =>
                        bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {
                        bots && is.array(bots.items) && [...bots.items]
                          .sort((a, b) => a.displayName.localeCompare(b.displayName))
                          .map(bot => {
                            const isDifferentLanguage = bot?.settings?.languages?.primary !== targetBot?.settings?.languages?.primary;

                            return (
                              <Option
                                key={bot.id}
                                value={bot.id}
                                disabled={isDifferentLanguage}
                                className={isDifferentLanguage ? 'text-muted' : undefined}
                              >
                                <Tooltip
                                  title={isDifferentLanguage ? t('Content replacement from a tenant with a different primary language is not supported.') : ''}
                                  placement="right"
                                >
                                  {
                                    isFetchingBots ? (
                                      <Skeleton active title={{ width: '80%' }} paragraph={false} className="mt-3 border-radius-base" />
                                    ) : (
                                      <BotAvatar
                                        bot={bot}
                                        avatarSize={24}
                                        isMuted={isDifferentLanguage}
                                      />
                                    )
                                  }
                                </Tooltip>
                              </Option>
                            );
                          })
                      }
                    </Select>
                  </Form.Item>
                </Col>
                <Col className="d-flex align-items-center">
                  <ArrowRightOutlined className="fs-lg"/>
                </Col>
                <Col xs={11}>
                  <Form.Item
                    name="targetBotId"
                    label={
                      <Row>
                        <Col span={24}>
                          <Typography.Text >
                            {
                              <Trans t={t}>
                                <Typography.Text className="text-secondary ml-3">
                                  To
                                  <Tooltip title={t('Replacing Agent content will overwrite all currently existing content of selected Agent.')}>
                                    <QuestionCircleOutlined className="ml-1"/>
                                  </Tooltip>
                                </Typography.Text>
                              </Trans>
                            }
                          </Typography.Text>
                        </Col>
                      </Row>
                    }
                    rules={[{
                      required: true,
                      message: t('Please choose a source agent.', { context: 'AI agent' })
                    }]}
                    validateStatus={showConflictMessage ? 'warning' : undefined}
                    help={showConflictMessage
                      ? t('This agent has a different primary language than the current one, which may lead to inconsistencies or issues in certain parts of the content.', { context: 'AI agent' })
                      : undefined
                    }
                  >
                    <Select
                      placeholder={t('Choose an agent', { context: 'AI agent' })}
                      size="large"
                      disabled
                      className="fs-base mb-1"
                      loading={isFetchingBots}
                      showSearch
                      onChange={checkForLanguageConflicts}
                      filterOption={(input, option) =>
                        bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {
                        bots && is.array(bots.items) && [...bots.items]
                          .sort((a, b) => a.displayName.localeCompare(b.displayName))
                          .map(bot => {
                            const isDifferentLanguage = bot?.settings?.languages?.primary !== targetBot?.settings?.languages?.primary;

                            return (
                              <Option
                                key={bot.id}
                                value={bot.id}
                                disabled={isDifferentLanguage}
                                className={isDifferentLanguage ? 'text-muted' : undefined}
                              >
                                <Tooltip
                                  title={isDifferentLanguage ? t('Content replacement from a tenant with a different primary language is not supported.') : ''}
                                  placement="right"
                                >
                                  {
                                    isFetchingBots ? (
                                      <Skeleton active title={{ width: '80%' }} paragraph={false} className="mt-3 border-radius-base" />
                                    ) : (
                                      <BotAvatar
                                        bot={bot}
                                        avatarSize={24}
                                        isMuted={isDifferentLanguage}
                                      />
                                    )
                                  }
                                </Tooltip>
                              </Option>
                            );
                          })
                      }
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="end">
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={isFetchingBots || isReplacingBotContent}
                  disabled={isFetchingBots || isReplacingBotContent}
                >
                  {t('Replace')}
                </Button>
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>

      <Modal
        open={isProgressModalOpen}
        className="ant-modal-p-0"
        onCancel={() => {
          if (isReplacingBotContent) {
            return;
          }

          if (isUpdateSuccessful) {
            closeModal();
          }

          setIsProgressModalOpen(false);
        }}
        footer={null}
        style={{ maxWidth: 400 }}
        width={'95%'}
        centered
        destroyOnClose
        closable={false}
      >
        {renderProgressModalContents()}
      </Modal>
    </Fragment>
  );
};

BotContentReplaceModal.propTypes = {
  targetBot: PropTypes.object,
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  onSubmit: PropTypes.func
};

export default BotContentReplaceModal;
